import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../utils/trpc'

/**
 * @fileoverview eNPS Response Store
 *
 * Zustand store for managing eNPS survey response page state.
 * Handles execution data fetching and response submission.
 */

interface EnpsExecution {
	id: string
	enpsConfigId: string
	status: string
	questionText?: string
	followUpQuestionText?: string
}

interface EnpsResponseState {
	// Data
	execution: EnpsExecution | null
	isSubmitted: boolean

	// Loading states
	isLoadingExecution: boolean
	isSubmitting: boolean

	// Error handling
	error: string | null

	// Actions
	fetchExecution: (executionId: string) => Promise<void>
	submitResponse: (params: {
		executionId: string
		participantId: string
		score: number
		feedback?: string
	}) => Promise<void>
	reset: () => void
	clearError: () => void
}

export const useEnpsResponseStore = create<EnpsResponseState>()(
	devtools(
		(set) => ({
			// Initial state
			execution: null,
			isSubmitted: false,
			isLoadingExecution: false,
			isSubmitting: false,
			error: null,

			// Fetch execution details
			fetchExecution: async (executionId: string) => {
				if (!executionId) return

				set({ isLoadingExecution: true, error: null })

				try {
					const result = await trpcClient.public.enps.getExecution.query({
						executionId,
					})

					set({
						execution: result as EnpsExecution,
						isLoadingExecution: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch survey details',
						isLoadingExecution: false,
					})
				}
			},

			// Submit response
			submitResponse: async ({ executionId, participantId, score, feedback }) => {
				set({ isSubmitting: true, error: null })

				try {
					await trpcClient.public.enps.submitResponse.mutate({
						executionId,
						participantId,
						score,
						feedback,
					})

					set({
						isSubmitted: true,
						isSubmitting: false,
					})
				} catch (error) {
					const message = error instanceof Error ? error.message : 'Failed to submit response. Please try again.'
					set({
						error: message,
						isSubmitting: false,
					})
					throw error // Re-throw so the component can handle it
				}
			},

			reset: () =>
				set({
					execution: null,
					isSubmitted: false,
					isLoadingExecution: false,
					isSubmitting: false,
					error: null,
				}),

			clearError: () => set({ error: null }),
		}),
		{ name: 'enps-response-store' },
	),
)
